import React, { useState } from 'react';

const UserGuide = ({ onClose }) => {
  const [activeSection, setActiveSection] = useState('overview');

  const sections = {
    overview: {
      title: 'System Overview',
      icon: '🏪',
      content: (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Restaurant QR Ordering System</h3>
          <p className="text-gray-700">
            A comprehensive digital ordering solution that allows restaurants to manage orders, menus, payments, and analytics through QR code-based customer ordering.
          </p>
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">Key Benefits:</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Contactless ordering via QR codes</li>
              <li>• Real-time order management</li>
              <li>• Integrated payment processing</li>
              <li>• Comprehensive analytics and reporting</li>
              <li>• Automated data management</li>
            </ul>
          </div>
        </div>
      )
    },
    gettingStarted: {
      title: 'Getting Started',
      icon: '🚀',
      content: (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Complete Setup Guide</h3>
          
          {/* Step 1 */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-2">Set Profile Password</h4>
                <p className="text-sm text-gray-600 mb-2">Before accessing profile settings, you must set a profile password.</p>
                <div className="bg-blue-50 p-3 rounded-md">
                  <p className="text-sm text-blue-900 font-medium mb-1">Navigation:</p>
                  <ul className="text-xs text-blue-800 space-y-1">
                    <li>• Click the <strong className="text-purple-600">🔒 Set Profile Password</strong> button in the top header (purple button)</li>
                    <li>• Check your email for the password setup link</li>
                    <li>• Click the link and create your profile password (min 6 characters)</li>
                    <li>• This password is used to access all Profile settings</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-2">Complete Restaurant Profile</h4>
                <p className="text-sm text-gray-600 mb-2">Fill in your restaurant information.</p>
                <div className="bg-blue-50 p-3 rounded-md">
                  <p className="text-sm text-blue-900 font-medium mb-1">Navigation:</p>
                  <ul className="text-xs text-blue-800 space-y-1">
                    <li>• Click <strong>Profile</strong> tab → Enter your profile password when prompted</li>
                    <li>• Select <strong>Restaurant Info</strong> sub-tab (default view)</li>
                    <li>• Click <strong>✏️ Edit Profile</strong> button</li>
                    <li>• Fill in: Name, Address, Phone, Cuisine Type, Opening Hours</li>
                    <li>• Click <strong>Save Changes</strong></li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-2">Configure Bill Settings (Taxes & Discounts)</h4>
                <p className="text-sm text-gray-600 mb-2">Set up taxes, service charges, and discounts.</p>
                <div className="bg-blue-50 p-3 rounded-md">
                  <p className="text-sm text-blue-900 font-medium mb-1">Navigation:</p>
                  <ul className="text-xs text-blue-800 space-y-1">
                    <li>• Go to <strong>Profile</strong> tab → <strong>Bill Settings</strong> sub-tab</li>
                    <li>• Click <strong>➕ Add Bill Component</strong></li>
                    <li>• Enter name (e.g., "GST", "Service Charge", "Discount")</li>
                    <li>• Select type: Percentage or Fixed amount</li>
                    <li>• Enter value (use negative for discounts, e.g., -10 for 10% discount)</li>
                    <li>• Click <strong>Save Component</strong></li>
                    <li>• Add multiple components as needed (taxes, service charges, discounts)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Step 4 */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">4</span>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-2">Create Menu Categories</h4>
                <p className="text-sm text-gray-600 mb-2">Organize your menu with categories.</p>
                <div className="bg-blue-50 p-3 rounded-md">
                  <p className="text-sm text-blue-900 font-medium mb-1">Navigation:</p>
                  <ul className="text-xs text-blue-800 space-y-1">
                    <li>• Go to <strong>Profile</strong> tab → <strong>Categories</strong> sub-tab</li>
                    <li>• Click <strong>Add New Category</strong></li>
                    <li>• Enter category name (e.g., "Starters", "Main Course", "Desserts")</li>
                    <li>• Optionally add description</li>
                    <li>• Click <strong>Add Category</strong></li>
                    <li>• Repeat for all categories</li>
                    <li>• Use drag handles to reorder categories</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Step 5 */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">5</span>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-2">Add Menu Items</h4>
                <p className="text-sm text-gray-600 mb-2">Populate your menu with dishes and prices.</p>
                <div className="bg-blue-50 p-3 rounded-md">
                  <p className="text-sm text-blue-900 font-medium mb-1">Navigation:</p>
                  <ul className="text-xs text-blue-800 space-y-1">
                    <li>• Go to <strong>Profile</strong> tab → <strong>Menu Items</strong> sub-tab</li>
                    <li>• Click <strong>Add New Item</strong></li>
                    <li>• Fill in item details:</li>
                    <li className="ml-4">- Name (e.g., "Chicken Biryani")</li>
                    <li className="ml-4">- Description</li>
                    <li className="ml-4">- Price</li>
                    <li className="ml-4">- Select category</li>
                    <li className="ml-4">- Mark as Veg/Non-veg</li>
                    <li className="ml-4">- Set availability status</li>
                    <li>• Click <strong>Add Item</strong></li>
                    <li>• Repeat for all menu items</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Step 6 */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">6</span>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-2">Create Tables & Generate QR Codes</h4>
                <p className="text-sm text-gray-600 mb-2">Set up tables and generate QR codes for customer ordering.</p>
                <div className="bg-blue-50 p-3 rounded-md">
                  <p className="text-sm text-blue-900 font-medium mb-1">Navigation:</p>
                  <ul className="text-xs text-blue-800 space-y-1">
                    <li>• Go to <strong>Profile</strong> tab → <strong>Tables & QR</strong> sub-tab</li>
                    <li>• Click <strong>Add New Table</strong></li>
                    <li>• Enter table number (e.g., "A1", "A2", "B1")</li>
                    <li>• Set seating capacity (optional)</li>
                    <li>• Click <strong>Add Table</strong></li>
                    <li>• For each table:</li>
                    <li className="ml-4">- Click <strong>📱 View QR</strong> to see QR code</li>
                    <li className="ml-4">- Click <strong>⬇️ Download QR</strong> to save as image</li>
                    <li className="ml-4">- Print and place on respective tables</li>
                    <li>• Also download <strong>Kitchen Display QR</strong> for staff</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Step 7 */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">7</span>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-2">Purchase Order Capacity</h4>
                <p className="text-sm text-gray-600 mb-2">Buy order capacity to enable customer ordering.</p>
                <div className="bg-blue-50 p-3 rounded-md">
                  <p className="text-sm text-blue-900 font-medium mb-1">Navigation:</p>
                  <ul className="text-xs text-blue-800 space-y-1">
                    <li>• Click <strong>Purchase Capacity</strong> button in header</li>
                    <li>• Select number of packages (50 orders per package at $10)</li>
                    <li>• Click <strong>Purchase</strong></li>
                    <li>• Complete payment via Stripe (if required)</li>
                    <li>• Your order capacity will be updated</li>
                    <li>• Each customer order consumes 1 capacity unit</li>
                  </ul>
                </div>
                <div className="bg-yellow-50 p-2 rounded-md mt-2">
                  <p className="text-xs text-yellow-800">⚠️ <strong>Important:</strong> You need order capacity before customers can place orders!</p>
                </div>
              </div>
            </div>
          </div>

          {/* Step 8 */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <span className="flex-shrink-0 w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold">✓</span>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-2">Setup Complete - Start Accepting Orders!</h4>
                <p className="text-sm text-gray-600 mb-2">Your restaurant is now ready to accept customer orders.</p>
                <div className="bg-green-50 p-3 rounded-md">
                  <p className="text-sm text-green-900 font-medium mb-1">What happens next:</p>
                  <ul className="text-xs text-green-800 space-y-1">
                    <li>• Customers scan table QR codes</li>
                    <li>• They browse menu and place orders</li>
                    <li>• Orders appear in <strong>Orders</strong> tab → <strong>Order Management</strong></li>
                    <li>• Kitchen staff see orders on Kitchen Display</li>
                    <li>• You can update order status and manage everything from dashboard</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-4 rounded-lg text-white">
            <p className="text-sm font-medium">🎉 Congratulations! Your QR ordering system is ready to use.</p>
          </div>
        </div>
      )
    },
    dailyOperations: {
      title: 'Daily Operations',
      icon: '🔄',
      content: (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Using the System Daily</h3>
          
          {/* Managing Orders */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
              <span className="mr-2">📋</span>
              Managing Incoming Orders
            </h4>
            <div className="space-y-3">
              <div className="bg-gray-50 p-3 rounded-md">
                <p className="text-sm text-gray-900 font-medium mb-1">Navigation:</p>
                <ul className="text-xs text-gray-700 space-y-1">
                  <li>• Go to <strong>Orders</strong> tab → <strong>Order Management</strong> sub-tab</li>
                  <li>• View all active orders from the last 24 hours</li>
                  <li>• Each order card shows:</li>
                  <li className="ml-4">- Order ID and time</li>
                  <li className="ml-4">- Table number</li>
                  <li className="ml-4">- Customer name and phone</li>
                  <li className="ml-4">- Ordered items with quantities</li>
                  <li className="ml-4">- Total amount</li>
                  <li className="ml-4">- Payment and order status</li>
                </ul>
              </div>
              <div className="bg-blue-50 p-3 rounded-md">
                <p className="text-sm text-blue-900 font-medium mb-1">Updating Order Status:</p>
                <ul className="text-xs text-blue-800 space-y-1">
                  <li>• Click the <strong>status dropdown</strong> on any order</li>
                  <li>• Select new status:</li>
                  <li className="ml-4">- <strong>Pending</strong>: New order received</li>
                  <li className="ml-4">- <strong>Preparing</strong>: Kitchen is preparing</li>
                  <li className="ml-4">- <strong>Ready</strong>: Food is ready for serving</li>
                  <li className="ml-4">- <strong>Completed</strong>: Order served to customer</li>
                  <li className="ml-4">- <strong>Cancelled</strong>: Order cancelled</li>
                  <li>• Status updates in real-time</li>
                </ul>
              </div>
              <div className="bg-green-50 p-3 rounded-md">
                <p className="text-sm text-green-900 font-medium mb-1">Order Actions:</p>
                <ul className="text-xs text-green-800 space-y-1">
                  <li>• <strong>🧾 Print Bill:</strong> Generate receipt for customer</li>
                  <li>• <strong>Combine Orders:</strong> Merge multiple orders from same table</li>
                  <li>• View payment status and method</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Creating Manual Orders */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
              <span className="mr-2">➕</span>
              Creating Manual Orders (Walk-ins/Phone Orders)
            </h4>
            <div className="bg-gray-50 p-3 rounded-md">
              <p className="text-sm text-gray-900 font-medium mb-1">Navigation:</p>
              <ul className="text-xs text-gray-700 space-y-1">
                <li>• Go to <strong>Orders</strong> tab → <strong>Create Order</strong> sub-tab</li>
                <li>• Select table from dropdown</li>
                <li>• Enter customer name and phone</li>
                <li>• Add items to order:</li>
                <li className="ml-4">- Select category</li>
                <li className="ml-4">- Choose menu items</li>
                <li className="ml-4">- Adjust quantities</li>
                <li className="ml-4">- Add special instructions</li>
                <li>• Review order total (includes taxes/discounts)</li>
                <li>• Select payment method (Cash/Card/Pay Later)</li>
                <li>• Click <strong>Place Order</strong></li>
                <li>• Order appears in Order Management immediately</li>
              </ul>
            </div>
          </div>

          {/* Kitchen Display */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
              <span className="mr-2">👨‍🍳</span>
              Using Kitchen Display System (KDS)
            </h4>
            <div className="space-y-2">
              <div className="bg-purple-50 p-3 rounded-md">
                <p className="text-sm text-purple-900 font-medium mb-1">Setup:</p>
                <ul className="text-xs text-purple-800 space-y-1">
                  <li>• Go to <strong>Profile</strong> tab → <strong>Tables & QR</strong></li>
                  <li>• Scroll to bottom and download <strong>Kitchen Display QR</strong></li>
                  <li>• Display this QR code on kitchen tablet/screen</li>
                  <li>• Kitchen staff scan QR to access KDS (no login needed)</li>
                </ul>
              </div>
              <div className="bg-orange-50 p-3 rounded-md">
                <p className="text-sm text-orange-900 font-medium mb-1">Kitchen Staff View:</p>
                <ul className="text-xs text-orange-800 space-y-1">
                  <li>• See all pending and preparing orders</li>
                  <li>• Auto-refreshes every 10 seconds</li>
                  <li>• Shows order time, table, and items</li>
                  <li>• Update status directly from KDS</li>
                  <li>• Completed orders disappear from view</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Viewing Analytics */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
              <span className="mr-2">📊</span>
              Checking Business Analytics
            </h4>
            <div className="bg-gray-50 p-3 rounded-md">
              <p className="text-sm text-gray-900 font-medium mb-1">Navigation:</p>
              <ul className="text-xs text-gray-700 space-y-1">
                <li>• Go to <strong>Profile</strong> tab → <strong>Analytics</strong> sub-tab</li>
                <li>• Select time period (Daily/Weekly/Monthly)</li>
                <li>• View key metrics:</li>
                <li className="ml-4">- Total orders and revenue</li>
                <li className="ml-4">- Average order value</li>
                <li className="ml-4">- Tax breakdown by component</li>
                <li className="ml-4">- Popular menu items with sales</li>
                <li className="ml-4">- Peak hours (in your local time)</li>
                <li className="ml-4">- Daily revenue trends</li>
                <li>• Use insights to optimize menu and operations</li>
              </ul>
            </div>
          </div>

          {/* Downloading Reports */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
              <span className="mr-2">📄</span>
              Downloading Daily Reports
            </h4>
            <div className="bg-gray-50 p-3 rounded-md">
              <p className="text-sm text-gray-900 font-medium mb-1">Navigation:</p>
              <ul className="text-xs text-gray-700 space-y-1">
                <li>• Go to <strong>Profile</strong> tab → <strong>Daily Reports</strong> sub-tab</li>
                <li>• See list of available reports (one per day)</li>
                <li>• Each report shows order count and date</li>
                <li>• Click <strong>⬇️ Download</strong> for single day CSV</li>
                <li>• For multiple days:</li>
                <li className="ml-4">- Select start and end date</li>
                <li className="ml-4">- Click <strong>Download Combined Report</strong></li>
                <li>• CSV includes:</li>
                <li className="ml-4">- Order ID, Date, Time</li>
                <li className="ml-4">- Table, Customer details</li>
                <li className="ml-4">- Items ordered</li>
                <li className="ml-4">- Subtotal, Tax breakdown, Total</li>
                <li className="ml-4">- Summary with totals</li>
              </ul>
            </div>
            <div className="bg-blue-50 p-2 rounded-md mt-2">
              <p className="text-xs text-blue-800">💡 <strong>Auto-generation:</strong> Reports are created automatically when orders are 30+ hours old</p>
            </div>
          </div>

          {/* Managing Capacity */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
              <span className="mr-2">⚡</span>
              Managing Order Capacity
            </h4>
            <div className="space-y-2">
              <div className="bg-red-50 p-3 rounded-md">
                <p className="text-sm text-red-900 font-medium mb-1">Low/Zero Capacity Alerts:</p>
                <ul className="text-xs text-red-800 space-y-1">
                  <li>• Red banner appears when capacity is 0</li>
                  <li>• Yellow banner appears when capacity is below 10</li>
                  <li>• Customers cannot place orders with zero capacity</li>
                  <li>• Banners show in both Order Management and Profile tabs</li>
                </ul>
              </div>
              <div className="bg-green-50 p-3 rounded-md">
                <p className="text-sm text-green-900 font-medium mb-1">Purchasing More Capacity:</p>
                <ul className="text-xs text-green-800 space-y-1">
                  <li>• Click <strong>Purchase Capacity</strong> from:</li>
                  <li className="ml-4">- Header button</li>
                  <li className="ml-4">- Alert banner buttons</li>
                  <li>• Select number of packages needed</li>
                  <li>• Each package = 50 orders at $10</li>
                  <li>• Complete payment</li>
                  <li>• Capacity updates immediately</li>
                </ul>
              </div>
            </div>
          </div>

        </div>
      )
    },
    menuManagement: {
      title: 'Menu Management',
      icon: '📋',
      content: (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Menu & Items</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h4 className="font-semibold text-gray-800">Categories</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Create menu categories</li>
                <li>• Set category descriptions</li>
                <li>• Enable/disable categories</li>
                <li>• Reorder categories</li>
              </ul>
            </div>
            <div className="space-y-2">
              <h4 className="font-semibold text-gray-800">Menu Items</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Add items with names & prices</li>
                <li>• Set item descriptions</li>
                <li>• Mark items as available/unavailable</li>
                <li>• Assign items to categories</li>
              </ul>
            </div>
          </div>
          <div className="bg-green-50 p-3 rounded-lg">
            <p className="text-sm text-green-800"><strong>Tip:</strong> Keep menu items updated and mark unavailable items to improve customer experience.</p>
          </div>
        </div>
      )
    },
    tableManagement: {
      title: 'Tables & QR Codes',
      icon: '🪑',
      content: (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Table Setup</h3>
          <div className="space-y-3">
            <div>
              <h4 className="font-semibold text-gray-800">Adding Tables</h4>
              <p className="text-sm text-gray-600">Create tables with unique names (e.g., A1, A2, B1) and seating capacity.</p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800">QR Code Generation</h4>
              <p className="text-sm text-gray-600">Each table gets a unique QR code that customers scan to access the menu and place orders.</p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800">Kitchen Display QR</h4>
              <p className="text-sm text-gray-600">Generate a special QR code for kitchen staff to view and manage incoming orders.</p>
            </div>
          </div>
          <div className="bg-yellow-50 p-3 rounded-lg">
            <p className="text-sm text-yellow-800"><strong>Note:</strong> Print QR codes clearly and place them prominently on each table for easy customer access.</p>
          </div>
        </div>
      )
    },
    orderManagement: {
      title: 'Order Management',
      icon: '📝',
      content: (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Managing Orders</h3>
          <div className="space-y-3">
            <div>
              <h4 className="font-semibold text-gray-800">Order Flow</h4>
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded">Pending</span>
                <span className="text-gray-400">→</span>
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">Preparing</span>
                <span className="text-gray-400">→</span>
                <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded">Ready</span>
                <span className="text-gray-400">→</span>
                <span className="px-2 py-1 bg-green-100 text-green-800 rounded">Completed</span>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800">Key Features</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• View orders from last 24 hours</li>
                <li>• Update order status in real-time</li>
                <li>• Print order receipts</li>
                <li>• Track payment status</li>
                <li>• Create manual orders</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800">Kitchen Display System (KDS)</h4>
              <p className="text-sm text-gray-600">Use the Kitchen Display to view active orders without logging in. Updates automatically every 10 seconds.</p>
            </div>
          </div>
        </div>
      )
    },
    payments: {
      title: 'Payment Processing',
      icon: '💳',
      content: (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Payment & Billing</h3>
          <div className="space-y-3">
            <div>
              <h4 className="font-semibold text-gray-800">Stripe Integration</h4>
              <p className="text-sm text-gray-600">Configure Stripe publishable and secret keys for secure payment processing.</p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800">Bill Settings</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Add tax components (GST, service charge, etc.)</li>
                <li>• Set positive (tax) or negative (discount) values</li>
                <li>• Configure percentage-based calculations</li>
                <li>• Add component descriptions</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800">Payment Methods</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Credit/Debit Cards (via Stripe)</li>
                <li>• Pay Later option for dine-in customers</li>
                <li>• OTP verification for payment details</li>
              </ul>
            </div>
          </div>
          <div className="bg-red-50 p-3 rounded-lg">
            <p className="text-sm text-red-800"><strong>Security:</strong> All payment data is processed securely through Stripe. Never store card information directly.</p>
          </div>
        </div>
      )
    },
    analytics: {
      title: 'Analytics & Reports',
      icon: '📊',
      content: (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Business Analytics</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-semibold text-gray-800">Analytics Dashboard</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Total orders and revenue</li>
                <li>• Average order value</li>
                <li>• Popular menu items</li>
                <li>• Peak hours analysis</li>
                <li>• Payment method breakdown</li>
                <li>• Daily/weekly/monthly views</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800">Daily Reports</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Automated daily CSV reports</li>
                <li>• Orders archived after 30 hours</li>
                <li>• Historical data access</li>
                <li>• Custom date range reports</li>
                <li>• Combined CSV downloads</li>
              </ul>
            </div>
          </div>
          <div className="bg-purple-50 p-3 rounded-lg">
            <p className="text-sm text-purple-800"><strong>Data Management:</strong> Orders are automatically archived after 30 hours with daily reports generated for record keeping.</p>
          </div>
        </div>
      )
    },
    profile: {
      title: 'Profile & Settings',
      icon: '⚙️',
      content: (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Restaurant Profile</h3>
          <div className="space-y-3">
            <div>
              <h4 className="font-semibold text-gray-800">Basic Information</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Update restaurant name and description</li>
                <li>• Change contact details (phone, email)</li>
                <li>• Set restaurant address</li>
                <li>• Configure operating hours</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800">Order Capacity</h4>
              <p className="text-sm text-gray-600">Purchase additional order capacity to handle more concurrent orders during busy periods.</p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800">Currency Settings</h4>
              <p className="text-sm text-gray-600">Set your preferred currency for pricing and billing (USD, EUR, GBP, etc.).</p>
            </div>
          </div>
        </div>
      )
    },
    troubleshooting: {
      title: 'Troubleshooting',
      icon: '🛠️',
      content: (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Common Issues & Solutions</h3>
          <div className="space-y-3">
            <div className="border-l-4 border-blue-500 pl-3">
              <h4 className="font-semibold text-gray-800">QR Code Not Working</h4>
              <p className="text-sm text-gray-600">Ensure tables are properly configured and restaurant is active. Check internet connection.</p>
            </div>
            <div className="border-l-4 border-green-500 pl-3">
              <h4 className="font-semibold text-gray-800">Payment Issues</h4>
              <p className="text-sm text-gray-600">Verify Stripe keys are correctly configured. Check if order capacity is available.</p>
            </div>
            <div className="border-l-4 border-orange-500 pl-3">
              <h4 className="font-semibold text-gray-800">Orders Not Appearing</h4>
              <p className="text-sm text-gray-600">Orders older than 24 hours are archived. Check Daily Reports for historical data.</p>
            </div>
            <div className="border-l-4 border-purple-500 pl-3">
              <h4 className="font-semibold text-gray-800">Analytics Empty</h4>
              <p className="text-sm text-gray-600">Analytics combine recent orders and archived data. May take time to populate with new restaurants.</p>
            </div>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-sm text-gray-700"><strong>Need Help?</strong> Contact support for additional assistance with setup and configuration.</p>
          </div>
        </div>
      )
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-white rounded-lg sm:rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-blue-600 text-white p-3 sm:p-6 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <span className="text-xl sm:text-2xl">📚</span>
              <h2 className="text-base sm:text-2xl font-bold">User Guide</h2>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors p-1"
              data-testid="close-user-guide"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
          {/* Sidebar - Horizontal scroll on mobile, vertical sidebar on desktop */}
          <div className="md:w-64 bg-gray-50 border-b md:border-b-0 md:border-r border-gray-200 overflow-x-auto md:overflow-x-visible md:overflow-y-auto flex-shrink-0">
            <nav className="p-2 md:p-4 flex md:flex-col md:space-y-2 space-x-2 md:space-x-0">
              {Object.entries(sections).map(([key, section]) => (
                <button
                  key={key}
                  onClick={() => setActiveSection(key)}
                  className={`flex-shrink-0 md:w-full flex flex-col md:flex-row items-center md:space-x-3 px-2 py-2 md:px-3 rounded-lg text-center md:text-left text-xs md:text-sm transition-colors whitespace-nowrap ${
                    activeSection === key
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 hover:bg-gray-200'
                  }`}
                  data-testid={`guide-section-${key}`}
                >
                  <span className="text-base md:text-lg">{section.icon}</span>
                  <span className="font-medium mt-1 md:mt-0">{section.title}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-3 sm:p-6">
              <div className="mb-3 sm:mb-4">
                <div className="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4">
                  <span className="text-2xl sm:text-3xl">{sections[activeSection].icon}</span>
                  <h1 className="text-lg sm:text-2xl font-bold text-gray-900">{sections[activeSection].title}</h1>
                </div>
              </div>
              <div className="prose prose-sm max-w-none text-sm sm:text-base">
                {sections[activeSection].content}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-3 py-2 sm:px-6 sm:py-4 border-t border-gray-200 flex-shrink-0">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-1 sm:space-y-0">
            <p className="text-xs sm:text-sm text-gray-600">
              For support, contact your system administrator.
            </p>
            <div className="text-xs sm:text-sm text-gray-500">
              v2.0 • {new Date().toLocaleDateString()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserGuide;